import { campusDateTime } from './campusTime';

/** Canonical supported terms for every catalog-backed web experience. */
export const SEMESTERS = [
  { id: 'spring-2027', label: 'Spring 2027', startDate: '2027-01-19', endDate: '2027-05-03' },
  { id: 'winter-2027', label: 'Winter 2027', startDate: '2026-12-23', endDate: '2027-01-15' },
  {
    id: 'fall-2026', label: 'Fall 2026', startDate: '2026-09-01', endDate: '2026-12-10',
    importantDates: [
      { label: 'First 7-week courses end', date: '2026-10-20' },
      { label: 'Second 7-week courses begin', date: '2026-10-21' },
      { label: 'Thanksgiving recess begins', date: '2026-11-26' },
      { label: 'Regular classes end', date: '2026-12-10' },
      { label: 'Final exams begin', date: '2026-12-15' },
    ],
  },
  { id: 'summer-2026', label: 'Summer 2026', startDate: '2026-05-26', endDate: '2026-08-12' },
  { id: 'spring-2026', label: 'Spring 2026', startDate: '2026-01-20', endDate: '2026-05-04' },
  { id: 'fall-2025', label: 'Fall 2025', startDate: '2025-09-02', endDate: '2025-12-10' },
  { id: 'winter-2025', label: 'Winter 2025', startDate: '2024-12-23', endDate: '2025-01-17' },
  { id: 'spring-2025', label: 'Spring 2025', startDate: '2025-01-21', endDate: '2025-05-05' },
] as const;

export type SemesterId = (typeof SEMESTERS)[number]['id'];
export const DEFAULT_SEMESTER_ID: SemesterId = 'fall-2026';
export const SEMESTER_OPTIONS = SEMESTERS.map(({ id, label }) => ({ id, label }));
export const SEMESTER_BY_ID = Object.fromEntries(
  SEMESTERS.map((semester) => [semester.id, semester])
) as Record<SemesterId, (typeof SEMESTERS)[number]>;

export function getSemesterCalendarSummary(value: string | null | undefined, now = new Date()) {
  const semester = isSemesterId(value) ? SEMESTER_BY_ID[value] : null;
  if (!semester) return null;

  // Date-only campus events use a common UTC noon representation for arithmetic.
  // Device-local midnight would disagree with SSR and drift across DST boundaries.
  const start = new Date(`${semester.startDate}T12:00:00Z`);
  const end = new Date(`${semester.endDate}T12:00:00Z`);
  const today = new Date(`${campusDateTime(now).date}T12:00:00Z`);
  const days = (date: Date) => Math.round((date.getTime() - today.getTime()) / 86_400_000);
  const startDelta = days(start);
  const endDelta = days(end);

  const formatDate = (date: Date) => date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
  const importantDates = ('importantDates' in semester ? semester.importantDates : [])
    .map((event) => ({ ...event, dateValue: new Date(`${event.date}T12:00:00Z`) }))
    .filter((event) => event.dateValue >= today);
  if (startDelta > 0) {
    return { semester, start, end, importantDates, headline: `${semester.label} starts in ${startDelta} day${startDelta === 1 ? '' : 's'}`, detail: `Classes run ${formatDate(start)}–${formatDate(end)}.` };
  }
  if (endDelta >= 0) {
    return { semester, start, end, importantDates, headline: `${semester.label} ends in ${endDelta} day${endDelta === 1 ? '' : 's'}`, detail: `Classes began ${formatDate(start)} and end ${formatDate(end)}.` };
  }
  return { semester, start, end, importantDates: [], headline: `${semester.label} ended ${Math.abs(endDelta)} day${Math.abs(endDelta) === 1 ? '' : 's'} ago`, detail: `Classes ran ${formatDate(start)}–${formatDate(end)}.` };
}

export function isSemesterId(value: string | null | undefined): value is SemesterId {
  return SEMESTER_OPTIONS.some(({ id }) => id === value);
}

export function normalizeSemesterId(value: string | null | undefined): SemesterId {
  return isSemesterId(value) ? value : DEFAULT_SEMESTER_ID;
}

export function getSemesterLabel(value: string | null | undefined): string {
  return isSemesterId(value) ? SEMESTER_BY_ID[value].label : value || 'Unknown term';
}
